import React, { useEffect, useState, useRef, useMemo } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip, Button, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useStoreActions, useStoreState} from 'easy-peasy';

import useSearchWithParams from "../../../hooks/useSearchWithParams.jsx";
import { useToast } from "../../../hooks/useToast.jsx";
import PropTypes from 'prop-types';
import SelectItemsComponent from '../../widgets/selectItemsComponent/SelectItemsComponent.jsx';
import './ClientsFiltersComponent.scss';
import Loader from '../../widgets/Loader.jsx'
import HelperMetiers360 from "../../../services/HelpersMetiers360.js";
import SelectMultipleItemsComponent from "../../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent.jsx";
import AxonautLogoBubbleComponent from '../../widgets/axonautLogoBubble/AxonautLogoBubbleComponent.jsx';
import PipedriveLogoBubbleComponent from '../../widgets/pipedriveLogoBubble/PipedriveLogoBubbleComponent.jsx';

const ClientsFiltersComponent = (props) => {
    const { allClients, setFilteredClients, isClientsCRM = false, isAdmin = false, statsTime, setStatsTime } = props;

    const { searchInput, search } = useSearchWithParams({ placeholder: 'Rechercher par mots-clés (établissement, C.P., ville...)', delay: 400 });

    const toast = useToast();

    const [searchParam, setSearchParam] = useSearchParams();

    const { fetchContactsM360, fetchClientsCategories, searchClientsCRMIdsByContact } = useStoreActions(actions => actions.clients);
    const { contactsM360, clientsCategories, allAnomalyTypes } = useStoreState(state => state.clients);
    const { allClientStatus } = useStoreState(state => state.clientStatus);
    const { allTagOperations } = useStoreState(state => state.tagOperations);
    const { allRelationTypes } = useStoreState(state => state.relationTypes);
    const {fetchClientStatus} = useStoreActions(actions => actions.clientStatus);
    const {fetchTagOperations} = useStoreActions(actions => actions.tagOperations);
    const { fetchRelationTypes } = useStoreActions(actions => actions.relationTypes);

    const [areFiltersApplied, setAreFiltersApplied] = useState(false);
    const [isFirstLoadFilters, setIsFirstLoadFilters] = useState(true);
    const [isUpdatingFiltersFromUrl, setIsUpdatingFiltersFromUrl] = useState(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const [parentList, setParentList] = useState([]);

    const minDateRef = useRef("");
    const maxDateRef = useRef("");
    const currentSubscriberRef = useRef(false);
    const parentClientSelectorRef = useRef(null);
    const statusClientRef = useRef(null);
    const exclusiveContactsM360Ref = useRef(false);
    const exclusiveOpeM360Ref = useRef(false);
    const exclusiveCategoriesRef = useRef(false);
    const exclusiveRelationTypesRef = useRef(false);
    const testerOnlyRef = useRef(false);
    const haveInvoicesRef = useRef(false);
    const withAnomaliesOnlyRef = useRef(false);

    const [contactsSelected, setContactsSelected] = useState([]);
    const [anomalyPrioritiesSelected, setAnomalyPrioritiesSelected] = useState([]);
    const [anomalyCodesSelected, setAnomalyCodesSelected] = useState([]);
    const [opesM360Selected, setOpesM360Selected] = useState([]);
    const [categoriesSelected, setCategoriesSelected] = useState([]);
    const [relationTypesSelected, setRelationTypesSelected] = useState([]);
    const [clientLinkedFilter, setClientLinkedFilter] = useState('all');
    const [axonautFilter, setAxonautFilter] = useState('all');
    const [pipedriveFilter, setPipedriveFilter] = useState('all');

    const [isSearching, setIsSearching] = useState(false);

    const onlyTesterCorrespondingTags = allTagOperations.filter(tag => tag.isTester);

    const PRIORITY_LABEL = { HIGH: 'Haute', MEDIUM: 'Moyenne', LOW: 'Basse' };

    const anomalyPriorityOptions = useMemo(() =>
        [...new Set(allAnomalyTypes.map(t => t.priority))].map(p => ({ id: p, name: PRIORITY_LABEL[p] ?? p })),
        [allAnomalyTypes]
    );

    const anomalyCodeOptions = useMemo(() => {
        const availableTypes = anomalyPrioritiesSelected.length === 0
            ? allAnomalyTypes
            : allAnomalyTypes.filter(t => anomalyPrioritiesSelected.some(p => p.id === t.priority));
        return availableTypes.map(t => ({ id: t.code, name: `${t.code} — ${t.label}` }));
    }, [allAnomalyTypes, anomalyPrioritiesSelected]);

    useEffect(() => {
        const availableCodes = new Set(anomalyCodeOptions.map(opt => opt.id));
        setAnomalyCodesSelected(prev => {
            const filtered = prev.filter(a => availableCodes.has(a.id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [anomalyCodeOptions]);

    const setFiltersFromUrl = ({ contactsM360, clientsCategories, tagOperations, relationTypes }) => {

        setIsUpdatingFiltersFromUrl(true);

        const newSearchParamMinDate = searchParam.get('minSubEnd');
        const newSearchParamMaxDate = searchParam.get('maxSubEnd');
        const newSearchParamSub = searchParam.get('currentSubscriberOnly');
        const newSearchParamContacts = searchParam.get('contactsM360');
        const newSearchParamExclContacts = searchParam.get('contactsM360Exclusive');
        const newSearchParamTagOpe = searchParam.get('opeM360Selected');
        const newSearchParamExclOpe = searchParam.get('opeM360Exclusive');
        const newSearchParamParent = searchParam.get('parentSelected');
        const newSearchParamCategory = searchParam.get('categoriesSelected');
        const newSearchParamExclCategories = searchParam.get('categoriesExclusive');
        const newSearchParamStatus = searchParam.get('statusClientSelected');
        const newSearchParamRelationType = searchParam.get('relationTypesSelected');
        const newSearchParamExclRelationTypes = searchParam.get('relationTypesExclusive');
        const newSearchParamTester = searchParam.get('testerOnly');
        const newSearchParamClientLinked = searchParam.get('clientLinked');
        const newSearchParamHaveInvoices = searchParam.get('haveInvoices');
        const newSearchParamAxonaut = searchParam.get('axonaut');
        const newSearchParamPipedrive = searchParam.get('pipedrive');
        const newSearchParamAnomalyOnly = searchParam.get('anomalyOnly');
        const newSearchParamAnomalyPriorities = searchParam.get('anomalyPriorities');
        const newSearchParamAnomalyCodes = searchParam.get('anomalyCodes');

        if (newSearchParamMinDate && minDateRef?.current) {
            minDateRef.current.value = newSearchParamMinDate;
        }
        if (newSearchParamMaxDate && maxDateRef?.current) {
            maxDateRef.current.value = newSearchParamMaxDate;
        }
        if (newSearchParamSub && currentSubscriberRef?.current) {
            currentSubscriberRef.current.checked = JSON.parse(newSearchParamSub);
        }
        if (isClientsCRM && newSearchParamClientLinked && clientLinkedFilter) {
            setClientLinkedFilter(newSearchParamClientLinked);
        }
        if (isClientsCRM && newSearchParamHaveInvoices && haveInvoicesRef?.current) {
            haveInvoicesRef.current.checked = JSON.parse(newSearchParamHaveInvoices);
        }
        if (isClientsCRM && newSearchParamAxonaut) {
            setAxonautFilter(newSearchParamAxonaut);
        }
        if (isClientsCRM && newSearchParamPipedrive) {
            setPipedriveFilter(newSearchParamPipedrive);
        }
        if (isClientsCRM && newSearchParamAnomalyOnly && withAnomaliesOnlyRef?.current) {
            withAnomaliesOnlyRef.current.checked = JSON.parse(newSearchParamAnomalyOnly);
        }
        if (isClientsCRM && newSearchParamAnomalyPriorities) {
            const priorityIds = newSearchParamAnomalyPriorities.split(',');
            setAnomalyPrioritiesSelected(priorityIds.map(id => ({ id, name: PRIORITY_LABEL[id] ?? id })));
        }
        if (isClientsCRM && newSearchParamAnomalyCodes) {
            const codeIds = newSearchParamAnomalyCodes.split(',');
            setAnomalyCodesSelected(codeIds.map(id => ({ id, name: id })));
        }
        if (newSearchParamContacts) {
            const contactsIdSelected = newSearchParamContacts.split(",");
            let contactsM360Selected = contactsM360?.filter(contactM360 => 
                contactsIdSelected?.some(id => id == contactM360.id));
            if(contactsIdSelected?.some(id => id == 0)) contactsM360Selected?.unshift({id: 0, name: "-- Sans contacts M360 --"});
            setContactsSelected(contactsM360Selected);
        }
        if(newSearchParamExclContacts && exclusiveContactsM360Ref?.current) {
            exclusiveContactsM360Ref.current.checked = JSON.parse(newSearchParamExclContacts);
        }
        if (newSearchParamTagOpe) {
            const opeIdSelected = newSearchParamTagOpe.split(",");
            let opeM360Selected = tagOperations?.filter(tag => opeIdSelected.some(id => id == tag.id));
            if(opeIdSelected?.some(id => id == 0)) opeM360Selected?.unshift({id: 0, name: "-- Sans opé M360 --"});
            setOpesM360Selected(opeM360Selected);
        }
        if(newSearchParamExclOpe && exclusiveOpeM360Ref?.current) {
            exclusiveOpeM360Ref.current.checked = JSON.parse(newSearchParamExclOpe);
        }
        if (!isClientsCRM && newSearchParamParent && parentClientSelectorRef?.current) {
            parentClientSelectorRef.current.value = newSearchParamParent;
        }
        if (newSearchParamStatus && statusClientRef?.current) {
            statusClientRef.current.value = newSearchParamStatus;
        }
        if (newSearchParamCategory) {
            const categoryIdSelected = newSearchParamCategory.split(",");
            let catSelected = clientsCategories?.filter(cat => categoryIdSelected?.some(id => id == cat.id));
            if(categoryIdSelected?.some(id => id == 0)) catSelected?.unshift({id: 0, name: "-- Sans catégories --"});
            setCategoriesSelected(catSelected);
        }
        if(newSearchParamExclCategories && exclusiveCategoriesRef?.current) {
            exclusiveCategoriesRef.current.checked = JSON.parse(newSearchParamExclCategories);
        }
        if (newSearchParamRelationType) {
            const relationTypeIdSelected = newSearchParamRelationType.split(",");
            let relationTypeSelected = relationTypes?.filter(rT => relationTypeIdSelected.some(id => id == rT.id));
            if (relationTypeIdSelected?.some(id => id == 0)) relationTypeSelected?.unshift({ id: 0, name: "-- Sans nature de la relation --" });
            setRelationTypesSelected(relationTypeSelected);
        }
        if (newSearchParamExclRelationTypes && exclusiveRelationTypesRef?.current) {
            exclusiveRelationTypesRef.current.checked = JSON.parse(newSearchParamExclRelationTypes);
        }
        if (!isClientsCRM && newSearchParamTester && testerOnlyRef?.current) {
            testerOnlyRef.current.checked = JSON.parse(newSearchParamTester);
        }

        setIsFiltersOpen((newSearchParamMinDate && newSearchParamMinDate !== "")
            || (newSearchParamMaxDate && newSearchParamMaxDate !== "" )
            || (newSearchParamSub && newSearchParamSub !== "false")
            || (isClientsCRM && newSearchParamClientLinked && newSearchParamClientLinked !== "all")
            || (isClientsCRM && newSearchParamHaveInvoices && newSearchParamHaveInvoices !== "false")
            || (isClientsCRM && newSearchParamAxonaut && newSearchParamAxonaut !== "all")
            || (isClientsCRM && newSearchParamPipedrive && newSearchParamPipedrive !== "all")
            || (newSearchParamContacts && newSearchParamContacts !== "") || (newSearchParamExclContacts && newSearchParamExclContacts !== "false")
            || (newSearchParamTagOpe && newSearchParamTagOpe !== "") || (newSearchParamExclOpe && newSearchParamExclOpe !== "false")
            || (!isClientsCRM && newSearchParamParent && newSearchParamParent !== "")
            || (newSearchParamCategory && newSearchParamCategory !== "") || (newSearchParamExclCategories && newSearchParamExclCategories !== "false")
            || (newSearchParamRelationType && newSearchParamRelationType !== "") || (newSearchParamExclRelationTypes && newSearchParamExclRelationTypes !== "false")
            || (newSearchParamStatus && newSearchParamStatus !== "")
            || (!isClientsCRM && newSearchParamTester && newSearchParamTester !== "false")
            || (isClientsCRM && newSearchParamAnomalyOnly && newSearchParamAnomalyOnly !== "false")
            || (isClientsCRM && newSearchParamAnomalyPriorities && newSearchParamAnomalyPriorities !== "")
            || (isClientsCRM && newSearchParamAnomalyCodes && newSearchParamAnomalyCodes !== "")
        );

        setIsUpdatingFiltersFromUrl(false);

    }


    const applyFiltersOnTable = () => {
        const minDate = minDateRef.current?.value ?? null;        
        const maxDate = maxDateRef.current?.value ?? null;
        const currentSubscriber = currentSubscriberRef.current?.checked ?? false;
        const clientLinked = clientLinkedFilter ?? "all";
        const haveInvoices = haveInvoicesRef.current?.checked ?? false;
        const withAnomaliesOnly = withAnomaliesOnlyRef.current?.checked ?? false;
        const axonaut = axonautFilter ?? "all";
        const pipedrive = pipedriveFilter ?? "all";
        const parentId = parentClientSelectorRef.current?.value ?? null;
        const statusLabel = statusClientRef.current?.value ?? null;
        const contactsM360Exclusive = exclusiveContactsM360Ref.current?.checked ?? false;
        const opeM360Exclusive = exclusiveOpeM360Ref.current?.checked ?? false;
        const categoriesExclusive = exclusiveCategoriesRef.current?.checked ?? false;
        const relationTypesExclusive = exclusiveRelationTypesRef.current?.checked ?? false;
        const onlyTester = testerOnlyRef.current?.checked ?? false;

        const clientsToDisplay = allClients
            ?.filter(client => {
                const minSubDateCondition = !minDate || client.subscriptionEndDate >= minDate;
                const maxSubDateCondition = !maxDate || client.subscriptionEndDate <= maxDate;
                const currentSubscriberCondition = !currentSubscriber || new Date(client.subscriptionEndDate) >= new Date();
                const clientLinkedCondition = !isClientsCRM || clientLinked == "all" || clientLinked == "linked" && client.isSubscriber || clientLinked == "unlinked" && !client.isSubscriber;
                const haveInvoicesCondition = !isClientsCRM || !haveInvoices || client.invoices !== 0;
                const axonautCondition = !isClientsCRM || axonaut == "all" || (axonaut == "yes" && !!client.idAxonaut) || (axonaut == "no" && !client.idAxonaut);
                const pipedriveCondition = !isClientsCRM || pipedrive == "all" || (pipedrive == "yes" && !!client.idPipedrive) || (pipedrive == "no" && !client.idPipedrive);
                const withAnomaliesOnlyCondition = !isClientsCRM || !withAnomaliesOnly || !!client.anomalyCodes;
                const anomalyCodes = client.anomalyCodes ? client.anomalyCodes.split(',').map(c => c.trim()) : [];
                const anomalyPriorityCondition = !isClientsCRM || anomalyPrioritiesSelected.length === 0
                    || anomalyCodes.some(code => {
                        const t = allAnomalyTypes.find(a => a.code === code);
                        return anomalyPrioritiesSelected.some(p => p.id === t?.priority);
                    });
                const anomalyCodeCondition = !isClientsCRM || anomalyCodesSelected.length === 0
                    || anomalyCodes.some(code => anomalyCodesSelected.some(a => a.id === code));
                const contactsRefCondition = contactsConditionFromSearch(client?.contactsM360, contactsM360Exclusive);
                const tagCondition = opeM360OrCategoriesConditionFromSearch(client?.tagOperationsName, opeM360Exclusive, opesM360Selected);
                const parentRefCondition = isClientsCRM || !parentId || client.parentUniqueId === parentId || (parentId === "aucun" && !client.parentUniqueId);
                const categoryRefCondition = opeM360OrCategoriesConditionFromSearch(client?.categories, categoriesExclusive, categoriesSelected);
                const statusRefCondition = !statusLabel || client.status === statusLabel || (statusLabel === "aucun" && !client.status);
                const relationTypeRefCondition = opeM360OrCategoriesConditionFromSearch(client?.clientRelationTypes, relationTypesExclusive, relationTypesSelected);
                const isTesterCondition = isClientsCRM || !onlyTester || opeM360OrCategoriesConditionFromSearch(client?.tagOperationsName, false, onlyTesterCorrespondingTags);

                return minSubDateCondition && maxSubDateCondition && currentSubscriberCondition && contactsRefCondition
                    && tagCondition && parentRefCondition && categoryRefCondition && statusRefCondition && relationTypeRefCondition && isTesterCondition
                    && clientLinkedCondition && haveInvoicesCondition && axonautCondition && pipedriveCondition
                    && withAnomaliesOnlyCondition && anomalyPriorityCondition && anomalyCodeCondition;
            })
            ?.filter(client => 
                HelperMetiers360.isSearchInText(search, client.clientName)
                || HelperMetiers360.isSearchInText(search, client.name)
                || HelperMetiers360.isSearchInText(search, client.addressZipCode)
                || HelperMetiers360.isSearchInText(search, client.addressCity)
                || HelperMetiers360.isSearchInText(search, client.tagOperationsName?.join())
                || HelperMetiers360.isSearchInText(search, client.contactsM360?.map(contact => contact.name)?.join())
                || HelperMetiers360.isSearchInText(search, client.categories?.join())
                || HelperMetiers360.isSearchInText(search, client.clientRelationTypes?.join())
                || HelperMetiers360.isSearchInText(search, client.status)
                || HelperMetiers360.isSearchInText(search, client.uai)
                || HelperMetiers360.isSearchInText(search, client.siret)
            );

        setFilteredClients(clientsToDisplay);
        setAreFiltersApplied(search || minDate || maxDate || currentSubscriber || parentId || clientLinkedFilter !== "all" || haveInvoices || axonautFilter !== "all" || pipedriveFilter !== "all"
            || contactsSelected.length > 0 || opesM360Selected.length > 0 || categoriesSelected.length > 0 || relationTypesSelected.length > 0
            || statusLabel || onlyTester || withAnomaliesOnly
            || anomalyPrioritiesSelected.length > 0 || anomalyCodesSelected.length > 0);

    }

    const contactsConditionFromSearch = (clientContactsM360, contactsM360Exclusive) => {
        if(contactsSelected.length === 0) {
            return true;
        } else if(contactsM360Exclusive) {
            if(contactsSelected.length === 1 && contactsSelected[0].id == 0) {
                return !clientContactsM360?.length;
            } else {
                return clientContactsM360?.map(contactM360 => contactM360.id).sort().join() 
                    == contactsSelected?.map(contactM360 => contactM360.id).sort().join()
            }
        } else {
            return (contactsSelected[0].id == 0 && !clientContactsM360?.length)
                || clientContactsM360?.some(contact => 
                    contactsSelected?.some(contactSelected => contact.id == contactSelected.id)) 
        }
    }

    const opeM360OrCategoriesConditionFromSearch = (clientList, isExclusive, listSelected) => {
        if(listSelected.length === 0) {
            return true;
        } else if(isExclusive) {
            if(listSelected.length === 1 && listSelected[0].id == 0) {
                return !clientList?.length;
            } else {
                return clientList?.sort().join() 
                    == listSelected?.map(item => item.name).sort().join()
            }
        } else {
            return (listSelected[0].id == 0 && !clientList?.length)
                || clientList?.some(item => 
                    listSelected?.some(itemSelected => item == itemSelected.name)) 
        }
    }

    const setParamsToUrl = (newParams) => {
        const paramsToSet = {}
        for (const [key, value] of searchParam.entries()) {
            paramsToSet[key] = value;
        }
        for (const [key, value] of newParams) {
            paramsToSet[key] = value;
        }
        setSearchParam(paramsToSet);
    }

    const resetFilters = () => {
        setParamsToUrl([
            ['search', ""],
            ['minSubEnd', ""],
            ['maxSubEnd', ""],
            ['currentSubscriberOnly', false],
            ['contactsM360', ""],
            ['contactsM360Exclusive', false],
            ['opeM360Selected', ""],
            ['opeM360Exclusive', false],
            ['parentSelected', ""],
            ['categoriesSelected', ""],
            ['categoriesExclusive', false],
            ['statusClientSelected', ""],
            ['relationTypesSelected', ""],
            ['relationTypesExclusive', false],
            ['testerOnly', false],
            ['clientLinked', "all"],
            ['haveInvoices', false],
            ['axonaut', "all"],
            ['pipedrive', "all"],
            ['anomalyOnly', false],
            ['anomalyPriorities', ""],
            ['anomalyCodes', ""],
        ]);
        if(minDateRef.current?.value) minDateRef.current.value = "";
        if(maxDateRef.current?.value) maxDateRef.current.value = "";
        if(currentSubscriberRef.current) currentSubscriberRef.current.checked = false;
        setContactsSelected([]);
        if(exclusiveContactsM360Ref.current) exclusiveContactsM360Ref.current.checked = false;
        setOpesM360Selected([]);
        if(exclusiveOpeM360Ref.current) exclusiveOpeM360Ref.current.checked = false;
        if (!isClientsCRM && parentClientSelectorRef.current?.value) parentClientSelectorRef.current.value = "";
        setCategoriesSelected([]);
        if(exclusiveCategoriesRef.current) exclusiveCategoriesRef.current.checked = false;
        if(statusClientRef.current?.value) statusClientRef.current.value = "";
        setRelationTypesSelected([]);
        if (exclusiveRelationTypesRef.current) exclusiveRelationTypesRef.current.checked = false;
        if (!isClientsCRM && testerOnlyRef.current) testerOnlyRef.current.checked = false;
        isClientsCRM && setClientLinkedFilter("all");
        if (isClientsCRM && haveInvoicesRef.current) haveInvoicesRef.current.checked = false;
        isClientsCRM && setAxonautFilter("all");
        isClientsCRM && setPipedriveFilter("all");
        if (isClientsCRM && withAnomaliesOnlyRef.current) withAnomaliesOnlyRef.current.checked = false;
        isClientsCRM && setAnomalyPrioritiesSelected([]);
        isClientsCRM && setAnomalyCodesSelected([]);

        applyFiltersOnTable();
    }


    const onFilterChange = () => {
        if (!isUpdatingFiltersFromUrl && !isFirstLoadFilters) {
            const paramsToUrl = [['search', search ?? ""]];
            isAdmin && paramsToUrl.push(['minSubEnd', minDateRef.current?.value ?? ""],
                ['maxSubEnd', maxDateRef.current?.value ?? ""],
                ['contactsM360', contactsSelected.length > 0 ? contactsSelected.map(contact => contact.id).join(',') : ""],
                ['contactsM360Exclusive', exclusiveContactsM360Ref?.current?.checked !== undefined ? exclusiveContactsM360Ref?.current.checked : false],
                ['opeM360Selected', opesM360Selected.length > 0 ? opesM360Selected.map(ope => ope.id).join(',') : ""],
                ['opeM360Exclusive', exclusiveOpeM360Ref?.current?.checked !== undefined ? exclusiveOpeM360Ref?.current.checked : false],
                ['categoriesSelected', categoriesSelected.length > 0 ? categoriesSelected.map(cat => cat.id).join(',') : ""],
                ['categoriesExclusive', exclusiveCategoriesRef?.current?.checked !== undefined ? exclusiveCategoriesRef?.current.checked : false],
                ['relationTypesSelected', relationTypesSelected.length > 0 ? relationTypesSelected.map(rT => rT.id).join(',') : ""],
                ['relationTypesExclusive', exclusiveRelationTypesRef?.current?.checked !== undefined ? exclusiveRelationTypesRef?.current.checked : false],
                ['currentSubscriberOnly', currentSubscriberRef?.current?.checked ?? ""],
                ['statusClientSelected', statusClientRef.current?.value ?? ""]);

            isClientsCRM
                ? paramsToUrl.push(
                    ['clientLinked', clientLinkedFilter ?? "all"],
                    ['haveInvoices', haveInvoicesRef?.current?.checked ?? ""],
                    ['axonaut', axonautFilter ?? "all"],
                    ['pipedrive', pipedriveFilter ?? "all"],
                    ['anomalyOnly', withAnomaliesOnlyRef?.current?.checked ?? false],
                    ['anomalyPriorities', anomalyPrioritiesSelected.length > 0 ? anomalyPrioritiesSelected.map(p => p.id).join(',') : ""],
                    ['anomalyCodes', anomalyCodesSelected.length > 0 ? anomalyCodesSelected.map(a => a.id).join(',') : ""]
                )
                : paramsToUrl.push(['parentSelected', parentClientSelectorRef.current?.value ?? ""], ['testerOnly', testerOnlyRef.current?.checked ?? ""]);

            setParamsToUrl(paramsToUrl)
        }
        applyFiltersOnTable();
    }


    useEffect(() => {
        const fetchData = async () => {
            try {
                const contactsM360Promise = !contactsM360.length ? fetchContactsM360() : Promise.resolve(null);
                const clientsCategoriesPromise = !clientsCategories.length ? fetchClientsCategories() : Promise.resolve(null);
                const clientStatusPromise = !allClientStatus.length ? fetchClientStatus() : Promise.resolve(null);
                const tagOperationsPromise = !allTagOperations.length ? fetchTagOperations() : Promise.resolve(null);
                const relationTypesPromise = !allRelationTypes.length ? fetchRelationTypes() : Promise.resolve(null);
    
                const [contactsM360Result, clientsCategoriesResult, clientStatusResult, tagOperationsResult, relationTypesResult] = await Promise.all([
                    contactsM360Promise,
                    clientsCategoriesPromise,
                    clientStatusPromise,
                    tagOperationsPromise,
                    relationTypesPromise
                ]);
    
                !isClientsCRM && setParentList(allClients.filter(client => client.childrenCount > 0));
    
                setFiltersFromUrl({
                    contactsM360: contactsM360Result || contactsM360,
                    clientsCategories: clientsCategoriesResult || clientsCategories,
                    tagOperations: tagOperationsResult || allTagOperations,
                    relationTypes: relationTypesResult || allRelationTypes
                });
    
            } catch (error) {
                toast.open({ message: "Erreur lors de la récupération des filtres", variant: "danger" });
            } finally {
                setIsFirstLoadFilters(false);
            }
        };
    
        if (isAdmin) { fetchData() } else { setIsFirstLoadFilters(false) };
    }, []);
    

    useEffect(() => {
        onFilterChange();
    }, [search, allClients, contactsSelected, opesM360Selected, categoriesSelected, relationTypesSelected, isFirstLoadFilters, clientLinkedFilter, axonautFilter, pipedriveFilter, anomalyPrioritiesSelected, anomalyCodesSelected]);

    const searchByContact = () => {
        if (search) {
            setIsSearching(true);
            searchClientsCRMIdsByContact(search)
                .then((res) => {
                    if (res[0]?.length) {
                        const matchingIds = new Set(res[0].map(id => id.uniqueId));
                        const clientsFound = allClients.filter(c => matchingIds.has(c.uniqueId));
                        setFilteredClients(clientsFound);
                    }
                })
                .finally(() => setIsSearching(false));
        }
    };

    return (<>
        <Row className={"d-flex align-items-center pe-0"}>
            {isAdmin &&
            <Col className="d-flex align-items-center">
                <Button id="btnFilters" className="cursor-pointer shadow"
                    onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                    onKeyDown={() => setIsFiltersOpen(!isFiltersOpen)}>
                    <p className="me-3">Filtres</p>
                    { isFiltersOpen 
                        ? <i className="fa fa-caret-up"></i>
                        : <i className="fa fa-caret-down"></i>
                    }
                </Button>
                { areFiltersApplied && 
                    <OverlayTrigger placement="top"
                        overlay={ <Tooltip>Effacer les filtres</Tooltip> }>
                        <Button id="btnInitFilters" className="shadow" variant="danger" size="sm" onClick={() => { resetFilters()} }>
                            <i className="fas fa-redo"></i>
                        </Button>
                    </OverlayTrigger>
                }
                { isFirstLoadFilters && 
                    <div id="loader-first-loading-filter"><Loader/></div>
                }
                </Col>}
            <Col className="pe-0">
                <div className="searchBlock">
                    {searchInput}
                    {isClientsCRM && search &&
                        <OverlayTrigger
                            placement="left"
                            overlay={
                                <Tooltip>
                                    Rechercher par contacts (email, téléphone)
                                </Tooltip>
                            }
                        >
                            <span>
                                <Button className="contactSearch" onClick={searchByContact}>
                                    {isSearching
                                        ? <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                                        : <i className="fas fa-address-book" />
                                    }
                                </Button>
                            </span>
                        </OverlayTrigger>}
                </div>
            </Col>
        </Row>
        {isAdmin &&
        <div id="filtersZone" className={!isFiltersOpen ? "hide-filters" : undefined}> 
            <Row className="d-flex align-items-center justify-content-between">
                <Col className="m360-col3-start" md={12} lg={6} xl={6} xxl={4}>    
                    <div className="d-flex align-items-center">
                        <label className="label-select" id="end-subscription-label">Fin d'abonnement&nbsp;: </label>
                        <input className="m-1" type="date" id="start" name="sub-start" max={maxDateRef.current?.value} 
                            ref={minDateRef} onChange={onFilterChange} />
                        <label htmlFor="end" className="m-0">au</label>
                        <input className="m-1" type="date" id="end" name="sub-end" min={minDateRef.current?.value}
                            ref={maxDateRef} onChange={onFilterChange} />
                    </div>
                </Col>
                <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                    <Form.Check id="currentSubscription" className="clickable"
                        label="Abonnements valides" onChange={onFilterChange} ref={currentSubscriberRef}/>
                </Col>
                <Col className="m360-col3-end" md={6} lg={6} xxl={4}>
                    <label className="label-select">Contacts&nbsp;M360&nbsp;: </label>
                    <span className="d-flex flex-column w-100">
                        <SelectMultipleItemsComponent itemsSelected={contactsSelected} setItemsSelected={setContactsSelected}
                            items={contactsM360} name="contacts" itemKey="id" itemValue="name"  id='select-contacts'
                            labelNone="contacts M360" />
                        { contactsSelected.length > 0 &&
                            <Form.Check id="exclusiveContactsM360" className="clickable" label="Exclusif"
                                onChange={onFilterChange} ref={exclusiveContactsM360Ref}/>
                        }
                    </span>
                </Col>
            </Row>
            <Row className="d-flex align-items-center justify-content-between mt-2">
                <Col className="m360-col3-start" md={6} lg={6} xxl={4}>
                    <label className="label-select">Opé&nbsp;M360&nbsp;: </label>
                    <span className="d-flex flex-column w-100">
                        <SelectMultipleItemsComponent itemsSelected={opesM360Selected} setItemsSelected={setOpesM360Selected}
                            items={allTagOperations} name="opeM360" itemKey="id" itemValue="name"  id='select-opeM360'
                            labelNone="opé M360" />
                        { opesM360Selected.length > 0 &&
                            <Form.Check id="exclusiveOpeM360" className="clickable" label="Exclusif"
                                onChange={onFilterChange} ref={exclusiveOpeM360Ref}/>
                        }
                    </span>
                </Col>
                    <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                    <label className="label-select">Catégories&nbsp;: </label>
                    <span className="d-flex flex-column w-100">
                        <SelectMultipleItemsComponent itemsSelected={categoriesSelected} setItemsSelected={setCategoriesSelected}
                            items={clientsCategories} name="categories" itemKey="id" itemValue="name"  id='select-categories'
                            labelNone="catégories" />
                        { categoriesSelected.length > 0 &&
                            <Form.Check id="exclusiveCategories" className="clickable" label="Exclusif"
                                onChange={onFilterChange} ref={exclusiveCategoriesRef}/>
                        }
                    </span>
                </Col>
                    <Col className="m360-col3-end" md={6} lg={6} xxl={4}>
                    <label className="label-select">Statut&nbsp;: </label>
                    <span className="d-flex flex-column w-100">
                        <SelectItemsComponent selectRef={statusClientRef} onChange={onFilterChange}
                                items={allClientStatus} itemKey="name" itemValue="name" labelNone={"statut"} />
                    </span>
                </Col>
                </Row>
                <Row className="d-flex align-items-center mt-2">
                    <Col className="m360-col3-start" md={6} lg={6} xxl={4}>
                        <label className="label-select">Nature&nbsp;de&nbsp;la&nbsp;relation&nbsp;: </label>
                        <span className="d-flex flex-column w-100">
                            <SelectMultipleItemsComponent itemsSelected={relationTypesSelected} setItemsSelected={setRelationTypesSelected}
                                items={allRelationTypes} name="relationType" itemKey="id" itemValue="name" id='select-relationType'
                                labelNone="nature de la relation" />
                            {relationTypesSelected.length > 0 &&
                                <Form.Check id="exclusiveRelationType" className="clickable" label="Exclusif"
                                    onChange={onFilterChange} ref={exclusiveRelationTypesRef} />
                            }
                        </span>
                    </Col>
                    {isClientsCRM
                        ? <>
                            <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                                <Form.Check id="haveInvoices" className="clickable"
                                    label="Seulement les clients facturés" onChange={onFilterChange} ref={haveInvoicesRef} />
                            </Col>
                            <Col className="m360-col3-end" md={6} lg={6} xxl={4}>
                                <div className="d-flex align-items-center">
                                    <label className="label-select">Clients liés à un établissement&nbsp;: </label>
                                    <div className='choices-selector-inline'>
                                        <div className={clientLinkedFilter === 'linked' ? 'selected' : null}
                                            onClick={() => setClientLinkedFilter('linked')} onKeyDown={() => setClientLinkedFilter('linked')}>
                                            Oui</div>
                                        <div className={clientLinkedFilter === 'all' ? 'selected' : null}
                                            onClick={() => setClientLinkedFilter('all')} onKeyDown={() => setClientLinkedFilter('all')}>
                                            Tous</div>
                                        <div className={clientLinkedFilter === 'unlinked' ? 'selected' : null}
                                            onClick={() => setClientLinkedFilter('unlinked')} onKeyDown={() => setClientLinkedFilter('unlinked')}>
                                            Non</div>
                                    </div>
                                </div>
                            </Col>
                            <Col className="m360-col3-end" md={6} lg={6} xxl={4}>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center gap-1">
                                        <AxonautLogoBubbleComponent />
                                        <div className='choices-selector-inline'>
                                            <div className={axonautFilter === 'yes' ? 'selected' : null}
                                                onClick={() => setAxonautFilter('yes')} onKeyDown={() => setAxonautFilter('yes')}>
                                                Oui</div>
                                            <div className={axonautFilter === 'all' ? 'selected' : null}
                                                onClick={() => setAxonautFilter('all')} onKeyDown={() => setAxonautFilter('all')}>
                                                Tous</div>
                                            <div className={axonautFilter === 'no' ? 'selected' : null}
                                                onClick={() => setAxonautFilter('no')} onKeyDown={() => setAxonautFilter('no')}>
                                                Non</div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <PipedriveLogoBubbleComponent />
                                        <div className='choices-selector-inline'>
                                            <div className={pipedriveFilter === 'yes' ? 'selected' : null}
                                                onClick={() => setPipedriveFilter('yes')} onKeyDown={() => setPipedriveFilter('yes')}>
                                                Oui</div>
                                            <div className={pipedriveFilter === 'all' ? 'selected' : null}
                                                onClick={() => setPipedriveFilter('all')} onKeyDown={() => setPipedriveFilter('all')}>
                                                Tous</div>
                                            <div className={pipedriveFilter === 'no' ? 'selected' : null}
                                                onClick={() => setPipedriveFilter('no')} onKeyDown={() => setPipedriveFilter('no')}>
                                                Non</div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </>
                        : <>
                            <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                                <div className="d-flex align-items-center">
                                    <label className="label-select">Enfants de&nbsp;: </label>
                                    <SelectItemsComponent selectRef={parentClientSelectorRef} onChange={onFilterChange}
                                        items={parentList} itemKey="uniqueId" itemValue="clientName" labelNone={"parent"} />
                                </div>
                            </Col>
                        </>}
                </Row>
                {isClientsCRM &&
                    <Row className="d-flex align-items-center mt-2">
                        <Col className="m360-col3-start" md={6} lg={4} xxl={3}>
                            <Form.Check id="withAnomaliesOnly" className="clickable"
                                label="Avec anomalies seulement" onChange={onFilterChange} ref={withAnomaliesOnlyRef} />
                        </Col>
                        {anomalyPriorityOptions.length > 0 &&
                            <Col className="m360-col3-center" md={6} lg={4} xxl={3}>
                                <label className="label-select">Priorité&nbsp;: </label>
                                <span className="d-flex flex-column w-100">
                                    <SelectMultipleItemsComponent
                                        itemsSelected={anomalyPrioritiesSelected}
                                        setItemsSelected={setAnomalyPrioritiesSelected}
                                        items={anomalyPriorityOptions}
                                        name="anomalyPriorities"
                                        itemKey="id" itemValue="name"
                                        id="select-anomalyPriorities"
                                        labelNone="priorités" />
                                </span>
                            </Col>
                        }
                        {anomalyCodeOptions.length > 0 &&
                            <Col className="m360-col3-end" md={6} lg={8} xxl={6}>
                                <label className="label-select">Anomalie&nbsp;: </label>
                                <span className="d-flex flex-column w-100">
                                    <SelectMultipleItemsComponent
                                        itemsSelected={anomalyCodesSelected}
                                        setItemsSelected={setAnomalyCodesSelected}
                                        items={anomalyCodeOptions}
                                        name="anomalyCodes"
                                        itemKey="id" itemValue="name"
                                        id="select-anomalyCodes"
                                        labelNone="anomalies" />
                                </span>
                            </Col>
                        }
                    </Row>
                }
                {!isClientsCRM &&
                    <Row className="d-flex align-items-center mt-2">
                        <Col className="m360-col3-start" md={6} lg={6} xxl={4}>
                            <Form.Check id="testerOnly" className="clickable"
                                label="Clients testeurs" onChange={onFilterChange} ref={testerOnlyRef} />
                        </Col>
                        {(statsTime && setStatsTime) && <Col className="m360-col3-center" md={6} lg={6} xxl={4}>
                            <div className="d-flex align-items-center">
                                <label className="label-select">Stats d'usage depuis&nbsp;: </label>
                                <div className='choices-selector-inline'>
                                    <div className={statsTime === 'allTime' ? 'selected' : null}
                                        onClick={() => setStatsTime('allTime')} onKeyDown={() => setStatsTime('allTime')}>
                                        Toujours</div>
                                    <div className={statsTime === 'oneMonth' ? 'selected' : null}
                                        onClick={() => setStatsTime('oneMonth')} onKeyDown={() => setStatsTime('oneMonth')}>
                                        30 jours</div>
                                </div>
                            </div>
                        </Col>}
                    </Row>
                }
            </div>}
    </>)
}

ClientsFiltersComponent.propTypes = {
    allClients: PropTypes.array.isRequired,
    setFilteredClients: PropTypes.any.isRequired,
    isClientsCRM: PropTypes.bool,
    isAdmin: PropTypes.bool,
    statsTime: PropTypes.string,
    setStatsTime: PropTypes.any
};

export default ClientsFiltersComponent